import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportXlsx } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, DownloadSimple, MagnifyingGlass, Trash, Package, Cube, Ruler } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { BulkUploadButton } from "@finance/components/BulkUpload";
import { useAuth } from "@finance/context/AuthContext";
import { ServiceMargin } from "@finance/components/InventoryAging";

const FIELDS = [
  ["sku", "SKU / Item Code"], ["name", "Name / Description"], ["manufacturer", "Manufacturer Code"], ["unit", "Unit"],
];

export default function Items() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const { user } = useAuth();
  const [sel, setSel] = useState([]);

  const load = useCallback(() => api.get("/items", { params: q ? { q } : {} }).then((r) => setRows(r.data)), [q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.sku || !form.name || !form.brand) { toast.error("SKU, Name and Brand are required"); return; }
    try {
      if (form.id) await api.put(`/items/${form.id}`, form);
      else await api.post("/items", form);
      toast.success("Item saved");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete item ${it.sku}?`)) return;
    try { await api.delete(`/items/${it.id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const delSelected = async () => {
    if (!window.confirm(`Delete ${sel.length} item(s)?`)) return;
    let fail = 0;
    for (const id of sel) { try { await api.delete(`/items/${id}`); } catch { fail += 1; } }
    toast.success(`Deleted ${sel.length - fail}${fail ? `, ${fail} blocked (in use)` : ""}`); setSel([]); load();
  };

  const cols = [{ key: "sku", label: "SKU" }, { key: "name", label: "Name" }, { key: "brand", label: "Brand" }, { key: "manufacturer", label: "Manufacturer" }, { key: "unit", label: "Unit" }];

  return (
    <Layout>
      <PageHeader
        title="Item Master"
        subtitle={`${rows.length} SKUs · canonical codes prevent description-based stock splits`}
        actions={
          <>
            {sel.length > 0 && <Button variant="outline" className="border-red-200 text-red-600" data-testid="delete-selected-items" onClick={delSelected}><Trash size={16} className="mr-1" />Delete ({sel.length})</Button>}
            <Button variant="outline" data-testid="export-items" onClick={() => exportXlsx("items", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <BulkUploadButton entity="items" onDone={load} />
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-item-btn" onClick={() => { setForm({ unit: "unit" }); setOpen(true); }}><Plus size={16} className="mr-1" />New Item</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={Package} label="Total SKUs" value={rows.length} testid="kpi-items-count" />
          <KpiCard icon={Cube} label="With Mfr Code" value={rows.filter((r) => r.manufacturer).length} tone="info" testid="kpi-items-mfr" />
          <KpiCard icon={Ruler} label="Distinct Units" value={new Set(rows.map((r) => r.unit)).size} tone="success" testid="kpi-items-units" />
        </KpiStrip>
        <div className="relative mb-4 w-72">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
          <Input data-testid="item-search" placeholder="Search SKU / name…" className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {rows.length === 0 ? (
          <EmptyState icon={Package} testid="items-empty" title="No items yet" message="Create SKUs using the manufacturer / item code, or bulk-upload a list." />
        ) : (
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-3 py-2"><input type="checkbox" data-testid="select-all-items" checked={rows.length > 0 && sel.length === rows.length} onChange={() => setSel(sel.length === rows.length ? [] : rows.map((r) => r.id))} /></th>
              <th className="px-4 py-2 text-left">SKU</th><th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Brand</th>
              <th className="px-4 py-2 text-left">Manufacturer</th><th className="px-4 py-2 text-left">Unit</th><th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id} data-testid={`item-row-${it.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2"><input type="checkbox" data-testid={`select-item-${it.id}`} checked={sel.includes(it.id)} onChange={() => toggle(it.id)} /></td>
                  <td className="px-4 py-2 font-mono-num font-medium">{it.sku}</td>
                  <td className="px-4 py-2">{it.name}</td>
                  <td className="px-4 py-2">{it.brand || "Others"}</td>
                  <td className="px-4 py-2 lx-num">{it.manufacturer || "—"}</td>
                  <td className="px-4 py-2">{it.unit}</td>
                  <td className="px-4 py-2" data-testid={`item-type-${it.id}`}><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${it.item_type === "Service" ? "bg-amber-100 text-amber-800" : "bg-[#E6ECF5] text-[#1B4383]"}`}>{(it.item_type || "Goods").toUpperCase()}</span></td>
                  <td className="px-4 py-2 text-right">
                    <button className="text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-item-${it.id}`} onClick={() => { setForm(it); setOpen(true); }}>Edit</button>
                    <button className="ml-3 text-xs text-gray-400 hover:text-red-500" data-testid={`delete-item-${it.id}`} onClick={() => del(it)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="item-dialog">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map(([key, label]) => (
              <div key={key} className={key === "name" ? "col-span-2" : ""}>
                <Label className="lx-label">{label}</Label>
                <Input className="mt-1" data-testid={`item-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </div>
            ))}
            <div className="col-span-2">
              <Label className="lx-label">Type (Goods / Service){form.id && !user?.is_owner ? " — owner only" : ""}</Label>
              <Select value={form.item_type || "Goods"} disabled={!!form.id && !user?.is_owner} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                <SelectTrigger className="mt-1" data-testid="item-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Goods">Goods</SelectItem><SelectItem value="Service">Service</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="lx-label">Brand *</Label>
              <Select value={form.brand || ""} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger className="mt-1" data-testid="item-brand"><SelectValue placeholder="Select brand (used for GL routing)" /></SelectTrigger>
                <SelectContent>{["Multotec", "Condra", "Dromex", "Elephant Lifting", "Nordbak", "Others"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="lx-label">Notes</Label>
              <Textarea className="mt-1" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="col-span-2 rounded-sm border border-gray-200 bg-gray-50 p-3">
              <div className="lx-label mb-2 text-gray-700">Opening Balance {form.id ? "(edit stock on hand)" : "(optional)"}</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="lx-label">Opening Qty</Label>
                  <Input type="number" className="mt-1" data-testid="item-opening-qty" value={form.opening_qty ?? ""} onChange={(e) => setForm({ ...form, opening_qty: e.target.value === "" ? null : Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="lx-label">Unit Cost (TZS)</Label>
                  <Input type="number" className="mt-1" data-testid="item-opening-cost" value={form.opening_unit_cost ?? ""} onChange={(e) => setForm({ ...form, opening_unit_cost: e.target.value === "" ? null : Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="lx-label">As-of Date</Label>
                  <DateField className="mt-1" data-testid="item-opening-date" value={(form.opening_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-gray-500">Posts opening stock value to Inventory (Dr) and Opening Balance Equity (Cr). Leave qty blank for none.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-item" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ServiceMargin />
    </Layout>
  );
}
